/**
 * 뷰 링크 공유 (사장님 확정 2026-08-08) — MY DOX 목록의 [URL]·[QR] 배선.
 *
 * ★ 전부 **위임**이다 — URL 팝업(dox_viewlink.html)은 nx.modal 이 fetch 로
 *   끼워 넣는 조각이라 그 안 <script> 는 실행되지 않는다(dox_icon 과 같은
 *   이유). 부팅 때 document 에 한 번만 건다.
 *
 * [복사] — data-nx-copy="<input id>": clipboard API, 실패하면 select() +
 *   execCommand 폴백. 성공하면 버튼 글자를 잠깐 data-copied 로 바꾼다.
 *
 * [QR]  — data-nx-qr="<viewlink 경로>": ?format=json 으로 {url, title, icon}
 *   을 받아, **인쇄 품질**(1200px 폭) 캔버스에 문서 아이콘 + 문서 이름 +
 *   QR 코드를 그려 PNG 로 내려받는다. QR 은 qrcode-generator(순수 JS,
 *   의존성 0)로 만든다 — 오차보정 M, 모듈을 크게 그려 저해상 인쇄에도 산다.
 *   커스텀 아이콘 이미지는 S3 presigned 라 CORS 취급이 필요하다
 *   (crossOrigin=anonymous). 그려지지 않으면(taint 등) **아이콘만 빼고**
 *   이름+QR 로 내려받는다 — 다운로드 자체가 죽으면 안 된다.
 */

import qrcode from 'qrcode-generator';

const W = 1200;                 // 인쇄 기준 폭(약 100mm @ 300DPI)
const QR_SIZE = 760;
/** ★ F564 화살표3 — 로고를 **강조 크기**로. 제목(64px)보다 크되 QR 을 안
 *  누르는 값이다(제목 두 줄 + QR 이 한 장에 들어가야 한다). */
const ICON_BOX = 240;
const TOP = 48;                 // F267①: 위 여백이 너무 컸다 — 딱 붙인다
/** ★ F564 화살표1·2 — 사이 간격 하나로 모은다. 자리마다 다른 수를 쓰면
 *  «위아래가 안 맞는다» 가 또 생긴다(사장님이 보신 그것). */
const GAP = 56;
const TITLE_FONT = '600 64px system-ui, -apple-system, "Malgun Gothic", sans-serif';
const TITLE_LH = 80;
const URL_LH = 56;
const POWERED_LH = 44;

interface QrData {
  url: string;
  code: string;
  title: string;
  icon?: string | null;
  /** ★ F564 화살표4 — 무료 호스팅인가(Powered by 부착 조건). 서버가 준다. */
  free_host?: boolean;
}

/** 그려 둔 그림 — 팝업의 [다운로드] 가 **다시 그리지 않고** 이것을 받는다. */
let qrReady: { cv: HTMLCanvasElement; name: string } | null = null;

/** Powered by 문구 — 사전에서 온다(화면에 영문을 손으로 박지 않는다). */
function poweredText(): string {
  const el = document.querySelector('[data-nx-i18n-qr-powered]') as HTMLElement | null;
  return (el?.textContent || 'Powered by PalmDox').trim();
}

function copyFallback(input: HTMLInputElement): boolean {
  try {
    input.focus();
    input.select();
    return document.execCommand('copy');
  } catch {
    return false;
  }
}

async function doCopy(btn: HTMLElement): Promise<void> {
  const input = document.getElementById(btn.dataset.nxCopy || '') as HTMLInputElement | null;
  if (!input) return;
  let ok = false;
  try {
    await navigator.clipboard.writeText(input.value);
    ok = true;
  } catch {
    ok = copyFallback(input);
  }
  if (ok) {
    const orig = btn.textContent;
    btn.textContent = btn.dataset.copied || orig;
    window.setTimeout(() => { btn.textContent = orig; }, 1500);
  }
}

function loadImage(src: string, cors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';   // S3 presigned — CORS 는 버킷 설정
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('icon load failed'));
    img.src = src;
  });
}

/**
 * ★ F267②(사장님): "DOX 이름 앞에 DOX 아이콘 **반드시** 들어가야 한다."
 *   커스텀 아이콘이 없으면 비워 두고 있었다 — 기본(로고 마크)을 그린다.
 *   마크를 여기 다시 그리면 두 벌이 된다(F221) — **화면에 이미 그려져 있는**
 *   그 행의 아이콘(.nx-dox-ico 안 img 또는 svg)을 그대로 가져와 쓴다.
 */
async function rowIconImage(btn: HTMLElement, iconUrl?: string | null):
    Promise<HTMLImageElement | null> {
  // ★★★★★ F564(사장님 2026-08-17, QR 그림 화살표3: *"이 DOX 의 로고(중앙
  //   위치·강조 크기)"*) — **로고가 아예 안 그려지고 있었다.**
  //   여기가 `btn.closest('tr')` 로 그 행을 찾았는데, MY DOX 목록은 F5xx 에서
  //   **표가 아니라 카드**가 됐다. `tr` 은 없으니 늘 null → 아이콘 없이 그렸고,
  //   사장님 캡처의 «위가 빈 자리»(화살표4)가 그것이다.
  //   ⇒ ⑴ 서버가 준 아이콘 주소를 **먼저** 쓰고(팝업에는 행이 아예 없다),
  //     ⑵ 없으면 그 카드 안의 아이콘, ⑶ 그것도 없으면 화면 어디든 있는
  //        기본 로고 마크를 쓴다. 셋 다 **이미 있는 그림**이다(F221).
  if (iconUrl) {
    try { return await loadImage(iconUrl, true); } catch { /* 다음 후보로 */ }
  }
  const box = (btn.closest('[data-nx-dox-row], tr, .nx-dc') as HTMLElement | null)
                ?.querySelector('.nx-dox-ico')
              || document.querySelector('.nx-dox-ico');
  if (!box) return null;
  const img = box.querySelector('img');
  if (img && img.src) {
    return loadImage(img.src, true);      // 커스텀 아이콘(S3 302) — CORS 필요
  }
  const svg = box.querySelector('svg');
  if (svg) {
    const clone = svg.cloneNode(true) as SVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    // 뷰박스만 있는 SVG 는 고유 크기가 0 이라 drawImage 가 안 그린다 — 박는다.
    clone.setAttribute('width', String(ICON_BOX));
    clone.setAttribute('height', String(ICON_BOX));
    const blob = new Blob([new XMLSerializer().serializeToString(clone)],
                          { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    try {
      return await loadImage(url, false); // 같은 문서의 blob — CORS 무관
    } finally {
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
  }
  return null;
}

function drawQr(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number): void {
  const qr = qrcode(0, 'M');      // 0 = 크기 자동
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const cell = Math.floor(size / n);
  const off = Math.floor((size - cell * n) / 2);
  ctx.fillStyle = '#000';
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (qr.isDark(r, c)) ctx.fillRect(x + off + c * cell, y + off + r * cell, cell, cell);
    }
  }
}

/** ★★★★★ D005(사장님 지시 2026-08-20): *"제목 줄바꿈없이 일자로"*
 *
 *  옛 판(`wrapTitle`)은 두 줄로 **접었다** — 사장님 캡처의 «… 매뉴 / 얼» 이
 *  그것이다. 이제 **한 줄**로 두고, 폭에 맞게 **글자 크기를 줄인다**(줄이는
 *  것이 접는 것보다 인쇄물에서 읽기 쉽다). 더 줄일 수 없을 때만 줄임표다.
 *
 *  ★ 크기를 «몇 px» 로 박지 않는다 — 실제로 **재서**(measureText) 맞춘다
 *    (0-1b: 글꼴은 기계마다 달라 계산으로 정하면 틀린다).
 *  ★ 바닥(MIN)이 있는 이유: 아무리 긴 제목도 읽을 수 있어야 한다. 바닥에
 *    닿으면 그때 줄임표로 자른다.
 */
const TITLE_PX = 64;              // 기준 크기(TITLE_FONT 와 같은 값)
const TITLE_PX_MIN = 34;          // 이보다 작아지면 줄임표로 자른다

function titleFontAt(px: number): string {
  return `600 ${Math.round(px)}px system-ui, -apple-system, "Malgun Gothic", sans-serif`;
}

/** 한 줄 제목 — 맞는 글자 크기와 (필요하면 자른) 글자를 함께 돌려준다. */
function fitTitle(ctx: CanvasRenderingContext2D, text: string, maxW: number):
    { text: string; px: number } {
  const t = String(text || '');
  if (!t) return { text: '', px: TITLE_PX };
  for (let px = TITLE_PX; px >= TITLE_PX_MIN; px -= 2) {
    ctx.font = titleFontAt(px);
    if (ctx.measureText(t).width <= maxW) return { text: t, px };
  }
  // 바닥 크기에서도 넘친다 — 그때만 줄임표. 자르는 셈도 **재서** 한다.
  ctx.font = titleFontAt(TITLE_PX_MIN);
  let cut = t;
  while (cut.length > 1 && ctx.measureText(cut + '…').width > maxW) {
    cut = cut.slice(0, -1);
  }
  return { text: cut + '…', px: TITLE_PX_MIN };
}

/** QR 그림 한 장을 만든다 — **그리는 곳은 여기 하나**다(팝업도 다운로드도
 *  이 캔버스를 쓴다). 반환은 캔버스 그대로 — 부르는 쪽이 보여 주든 받든 한다.
 *
 *  ★★★★★ F564(사장님 2026-08-17, QR 그림):
 *    화살표1·2 *"상부·하부 여백을 일치시켜 줘"* → 높이를 **그린 것에서** 재서
 *      위·아래를 똑같이 `TOP` 으로 둔다. 예전에는 높이를 손으로 더한 상수라
 *      아래가 남아 **아래만 넓어 보였다.**
 *    화살표3 *"이 DOX 의 로고(중앙 위치·강조 크기)"* → 로고를 가운데 위에
 *      키워 그린다(`ICON_BOX`). 안 그려지던 원인은 `rowIconImage` 에 적었다.
 *    화살표4 *"DOX 무료호스팅 경우만 표시"* → 무료 호스팅일 때만 아래에
 *      `Powered by` 한 줄. 판정은 **서버가 준 값**(`free_host`)이다.
 */
async function buildQrCanvas(btn: HTMLElement, data: QrData):
    Promise<HTMLCanvasElement | null> {
  const cv = document.createElement('canvas');
  const ctx0 = cv.getContext('2d');
  if (!ctx0) return null;

  // ── ① 먼저 **글자를 재서** 높이를 정한다(그린 뒤 남는 여백이 없게) ──
  ctx0.font = TITLE_FONT;
  //: D005 — 제목은 **한 줄**이다(크기를 줄여 맞춘다 — fitTitle).
  const fit = fitTitle(ctx0, data.title || '', W - 160);
  const powered = !!data.free_host;
  const titleH = TITLE_LH;                 // 언제나 한 줄
  const bodyH = ICON_BOX + GAP + titleH + GAP + QR_SIZE + GAP + URL_LH
              + (powered ? POWERED_LH : 0);
  cv.width = W;
  cv.height = TOP + bodyH + TOP;          // ★ 위·아래가 **같은 값**이다

  const ctx = cv.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.textAlign = 'center';

  let y = TOP;
  try {
    const img = await rowIconImage(btn, data.icon);
    if (img) {
      const scale = Math.min(ICON_BOX / (img.width || ICON_BOX),
                             ICON_BOX / (img.height || ICON_BOX));
      const iw = (img.width || ICON_BOX) * scale;
      const ih = (img.height || ICON_BOX) * scale;
      ctx.drawImage(img, (W - iw) / 2, y + (ICON_BOX - ih) / 2, iw, ih);
    }
  } catch {
    /* 로고가 안 그려져도 QR 은 산다 */
  }
  y += ICON_BOX + GAP;

  ctx.fillStyle = '#111';
  //: D005 — 한 줄. 크기는 fitTitle 이 재서 정한 값이다(상수를 박지 않는다).
  ctx.font = titleFontAt(fit.px);
  ctx.fillText(fit.text, W / 2, y + TITLE_LH * 0.78);
  y += TITLE_LH + GAP;

  drawQr(ctx, data.url, (W - QR_SIZE) / 2, y, QR_SIZE);
  y += QR_SIZE + GAP;

  ctx.fillStyle = '#666';
  ctx.font = '400 40px system-ui, -apple-system, sans-serif';
  ctx.fillText(data.url.replace(/^https?:\/\//, ''), W / 2, y + URL_LH * 0.7);
  y += URL_LH;

  if (powered) {
    ctx.fillStyle = '#9aa3ad';
    ctx.font = '400 32px system-ui, -apple-system, sans-serif';
    ctx.fillText(poweredText(), W / 2, y + POWERED_LH * 0.7);
  }
  return cv;
}

function saveCanvas(cv: HTMLCanvasElement, name: string): void {
  cv.toBlob((blob) => {
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  }, 'image/png');
}

async function fetchQrData(src: string): Promise<QrData | null> {
  const r = await fetch(`${src}?format=json`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!r.ok) return null;
  return await r.json() as QrData;
}

/** ★★★★ F565 — 팝업이 뜨면 **그 자리에 그려 넣는다.** 팝업 조각 안 <script>
 *  는 실행되지 않으므로(F262) modal 의 `nx:modal:load` 를 받아서 한다. */
async function fillQrPopup(box: HTMLElement): Promise<void> {
  const img = box.querySelector('[data-nx-qr-img]') as HTMLImageElement | null;
  const wait = box.querySelector('[data-nx-qr-wait]') as HTMLElement | null;
  const src = box.dataset.qrSrc || '';
  const data = src ? await fetchQrData(src) : null;
  if (!data) { if (wait) wait.hidden = true; return; }
  const cv = await buildQrCanvas(box, data);
  if (!cv || !img) { if (wait) wait.hidden = true; return; }
  img.src = cv.toDataURL('image/png');
  img.hidden = false;
  if (wait) wait.hidden = true;
  qrReady = { cv, name: `${box.dataset.qrFile || 'palmdox-qr'}-${data.code}.png` };
  const save = document.querySelector('[data-nx-qr-save]') as HTMLButtonElement | null;
  if (save) save.disabled = false;
}

export function initDoxShare(): void {
  document.addEventListener('click', (ev: Event) => {
    const t = ev.target as HTMLElement | null;
    if (!t) return;
    const copyBtn = t.closest('[data-nx-copy]') as HTMLElement | null;
    if (copyBtn) { void doCopy(copyBtn); return; }
    // ★★★★ F565 — [다운로드] 는 **팝업에 보이는 그 그림**을 받는다.
    //   다시 그리지 않으므로 화면과 파일이 어긋날 수가 없다.
    const save = t.closest('[data-nx-qr-save]') as HTMLElement | null;
    if (save) {
      if (qrReady) saveCanvas(qrReady.cv, qrReady.name);
      return;
    }
    /* ★ F581(감사 2026-08-18): 옛 「행에서 바로 받기」(`data-nx-qr`) 경로는
       **걷었다.** 그 표식을 내보내는 화면이 저장소에 하나도 없다(F565 로
       목록이 팝업으로 갔다) — 안 쓰는 길을 «보험» 이라며 남기지 않는다. */
  }, true);

  // ★★★★ F565 — 팝업이 실리면 그 자리에 그린다(조각 안 <script> 는 안 돈다).
  document.addEventListener('nx:modal:load', (ev: Event) => {
    const root = (ev as CustomEvent).detail?.root as HTMLElement | undefined;
    const box = root?.querySelector('[data-nx-qr-box]') as HTMLElement | null;
    if (box) {
      qrReady = null;                       // 앞 팝업의 그림을 안 물려준다
      void fillQrPopup(box).catch(() => { /* 조용히 — 대기 문구가 남는다 */ });
    }
  });
}
